import { useMemo, useState } from "react";

import clsx from "clsx";

import { TitleSlide } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { MetricValue } from "@shared/metric-value";
import Spinner from "@shared/spinner";

import { KPI_FILTER_EQUATION_LABELS } from "../../constants";
import { useUniversalBenchmarksConfiguratorContext } from "../../hooks";
import { type KpiFilterDescriptor, type KpiMetricsData } from "../../types";

import { MetricModal } from "./metric-modal";

interface Props {
  kpiFilter: KpiFilterDescriptor;
  kpiMetricsData?: KpiMetricsData;
  disabled?: boolean;
  onUpdate(value: KpiFilterDescriptor): void;
  onDelete(): void;
}

export const MetricCard = ({
  kpiFilter,
  kpiMetricsData,
  disabled,
  onUpdate,
  onDelete,
}: Props) => {
  const { buildKpiMetrics } = useUniversalBenchmarksConfiguratorContext();

  const [isEditing, setIsEditing] = useState(false);

  const metrics = useMemo(() => {
    return buildKpiMetrics(
      kpiFilter.attributionModel,
      kpiFilter.attributionWindow,
      kpiFilter.accountingMode,
    );
  }, [kpiFilter, buildKpiMetrics]);

  const metric = useMemo(() => {
    return metrics.find((e) => e.id === kpiFilter.metricId);
  }, [kpiFilter, metrics]);

  if (!metric) {
    return (
      <div className="h-[80px] w-[220px] border bg-white rounded-lg py-3 px-4 relative">
        <TitleSlide className="text-xs text-center">
          Could not find this Traffic Source.
        </TitleSlide>
      </div>
    );
  }

  return (
    <div>
      <div
        className={clsx(
          "h-[80px] w-[220px] border bg-white rounded-lg overflow-hidden",
          "flex flex-col justify-between border-gray-1",
        )}
      >
        <div className="pt-1.5 px-3 flex-1 flex flex-col">
          <div className="flex items-center justify-between gap-3">
            <div className="truncate text-xs font-bold">{metric.name}</div>
            <div className="shrink-0 flex items-center">
              <GenericTooltip
                noInfoCircle
                content={disabled ? null : "Configure"}
                overlayClassName="pointer-events-none"
                placement="top"
                mouseEnterDelay={500}
              >
                <button
                  disabled={disabled}
                  className={clsx(
                    "rounded-md h-6 w-6 flex-center text-xs -mr-1.5",
                    { "hover:bg-cosmic-3": !disabled },
                  )}
                  onClick={() => setIsEditing(true)}
                >
                  <i className="fa-regular fa-edit text-gray-1"></i>
                </button>
              </GenericTooltip>
            </div>
          </div>
          <div
            className={clsx(
              "flex-1 flex items-center gap-2 tracking-wide text-sm leading-6 mb-2",
            )}
          >
            {!kpiMetricsData || kpiMetricsData.status === "loading" ? (
              <Spinner />
            ) : (
              <>
                <div>{KPI_FILTER_EQUATION_LABELS[kpiFilter.equation]}</div>
                <div className="text-cosmic">
                  <MetricValue
                    as="span"
                    format={metric.format}
                    value={kpiFilter.value}
                    precision="exact"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div>
        <MetricModal
          isOpen={isEditing}
          initialMetricId={kpiFilter.metricId}
          initialEquation={kpiFilter.equation}
          initialValue={kpiFilter.value}
          initialFilters={kpiFilter.filters}
          initialAttributionModel={kpiFilter.attributionModel}
          initialAttributionWindow={kpiFilter.attributionWindow}
          initialAccountingMode={kpiFilter.accountingMode}
          onClose={() => {
            setIsEditing(false);
          }}
          onSubmit={(value) => {
            onUpdate({
              ...kpiFilter,
              metricId: value.metricId,
              equation: value.equation,
              value: value.value,
              filters: value.filters,
              attributionModel: value.attributionModel,
              accountingMode: value.accountingMode,
              attributionWindow: value.attributionWindow,
            });
            setIsEditing(false);
          }}
          onDelete={() => {
            setIsEditing(false);
            onDelete();
          }}
        />
      </div>
    </div>
  );
};
